import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Select,
  SelectItem,
  Textarea,
  Divider,
  Chip,
  Spinner,
  Switch,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { Plus, Trash2, ShoppingCart, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ordersService } from "@/services/ordersService";
import { menuService } from "@/services/menuService";
import { configService } from "@/services/configService";
import {
  OrderItemInput,
  OrderType,
  OrderItemSelectionInput,
  OrderInput,
} from "@/types/orders";
import { Product, ProductType, Category, Addon } from "@/types/menu";
import { Customer, CustomerAddress } from "@/types/customers";
import { PaymentMethod } from "@/types/config";
import { CustomerSearch } from "@/components/customers/CustomerSearch";

interface OrderFormProps {
  preselectedCustomer?: Customer | null;
}

export default function OrderForm({
  preselectedCustomer = null
}: OrderFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<
    Record<number, Product[]>
  >({});
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedProductTypes, setSelectedProductTypes] = useState<
    ProductType[]
  >([]);
  const [selectedProductTypeId, setSelectedProductTypeId] = useState<
    number | null
  >(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [filteredAddons, setFilteredAddons] = useState<Addon[]>([]);

  // Order Data
  const [orderItems, setOrderItems] = useState<OrderItemInput[]>([]);
  const [orderType, setOrderType] = useState<OrderType>("pickup");
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    preselectedCustomer
  );
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | null>(
    preselectedCustomer?.addresses?.[0] || null
  );
  const [notes, setNotes] = useState("");
  const [tip, setTip] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);

  // Item being added
  const [currentItem, setCurrentItem] = useState<{
    product_id: number;
    product_type_id: number | null;
    quantity: number;
    special_instructions: string;
    addons: Record<number, number[]>;
  }>({
    product_id: 0,
    product_type_id: null,
    quantity: 1,
    special_instructions: "",
    addons: {},
  });

  // Item edit modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);

  useEffect(() => {
    if (preselectedCustomer) {
      setSelectedCustomer(preselectedCustomer);
      if (preselectedCustomer.addresses && preselectedCustomer.addresses.length > 0) {
        const defaultAddress =
          preselectedCustomer.addresses.find((addr) => addr.is_default) ||
          preselectedCustomer.addresses[0];
        setSelectedAddress(defaultAddress);
      }
    }
  }, [preselectedCustomer]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch categories and products
        const fetchedCategories = await menuService.getCategories();

        setCategories(fetchedCategories);

        // Fetch all products
        const fetchedProducts = await menuService.getProducts();

        setProducts(fetchedProducts);

        // Group products by category
        const productsByCat: Record<number, Product[]> = {};

        fetchedProducts.forEach((product) => {
          if (product.categories && product.categories.length > 0) {
            const categoryId = product.categories[0].id;

            if (!productsByCat[categoryId]) {
              productsByCat[categoryId] = [];
            }
            productsByCat[categoryId].push(product);
          }
        });
        setProductsByCategory(productsByCat);

        // Fetch payment methods
        const fetchedPaymentMethods = await configService.getPaymentMethods();

        setPaymentMethods(fetchedPaymentMethods);

        // Fetch restaurant config for tax rate and delivery fee
        const restaurantConfig = await configService.getRestaurantConfig();

        setTaxRate(parseFloat(restaurantConfig.tax_rate));
        setDeliveryFee(restaurantConfig.delivery_fee || 0);
      } catch (err) {
        console.error("Error fetching order form data:", err);
        setError("Failed to load necessary data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Reset current item selection when category changes
  useEffect(() => {
    if (selectedCategory !== null) {
      const productsInCategory = productsByCategory[selectedCategory] || [];

      setFilteredProducts(productsInCategory);
      setSelectedProductId(null);
      setSelectedProduct(null);
      setSelectedProductTypes([]);
      setSelectedProductTypeId(null);

      // Reset current item
      setCurrentItem({
        product_id: 0,
        product_type_id: null,
        quantity: 1,
        special_instructions: "",
        addons: {},
      });
    }
  }, [selectedCategory, productsByCategory]);

  // Load product details when a product is selected
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (selectedProductId) {
        try {
          const productDetails =
            await menuService.getProductById(selectedProductId);

          setSelectedProduct(productDetails);

          // Update product types
          if (
            productDetails.product_types &&
            productDetails.product_types.length > 0
          ) {
            setSelectedProductTypes(productDetails.product_types);
            setSelectedProductTypeId(productDetails.product_types[0].id);

            // Update current item
            setCurrentItem({
              ...currentItem,
              product_id: selectedProductId,
              product_type_id: productDetails.product_types[0].id,
              addons: {},
            });
          } else {
            setSelectedProductTypes([]);
            setSelectedProductTypeId(null);

            // Update current item without type
            setCurrentItem({
              ...currentItem,
              product_id: selectedProductId,
              product_type_id: null,
              addons: {},
            });
          }
        } catch (error) {
          console.error("Error fetching product details:", error);
          setError("Failed to load product details");
        }
      }
    };

    if (selectedProductId) {
      fetchProductDetails();
    }
  }, [selectedProductId]);

  useEffect(() => {
    const loadAddonsForProductType = async () => {
      if (!selectedProductTypeId) return;

      // Mostrar indicador de carga si es necesario
      setLoading(true);

      try {
        // Obtener los addons específicos para este tipo de producto
        const addonsForType = await getAddonsByProductType(
          selectedProductTypeId
        );

        // Actualizar el estado con estos addons
        setFilteredAddons(addonsForType);
      } catch (error) {
        console.error(
          "Error al cargar addons para el tipo de producto:",
          error
        );
        setError("No se pudieron cargar los complementos para este tamaño");
      } finally {
        setLoading(false);
      }
    };

    // Cargar los addons para este tipo de producto
    loadAddonsForProductType();

    // Actualizar el item actual con el nuevo tipo de producto
    setCurrentItem((prev) => ({
      ...prev,
      product_type_id: selectedProductTypeId,
      // Reiniciamos las selecciones de addons porque cambiaron los addons disponibles
      addons: {},
    }));
  }, [selectedProductTypeId]);

  const getAddonsByProductType = async (productTypeId: number) => {
    try {
      // Usamos el endpoint específico para obtener los addons de un tipo de producto
      const addons = await menuService.getAddonsByProductType(productTypeId);

      return addons;
    } catch (error) {
      console.error("Error fetching addons for product type:", error);

      return [];
    }
  };

  // Calculate order totals
  const calculateTotals = () => {
    // Calculate subtotal from items
    let subtotal = orderItems.reduce(
      (sum, item) => sum + (item.total_price ?? 0) * item.quantity,
      0,
    );

    // Calculate tax
    const tax = Math.round(subtotal * (taxRate / 100));

    // Add delivery fee if applicable
    const fee = orderType === "delivery" ? deliveryFee : 0;

    // Total
    const total = subtotal + tax + fee + tip;

    return {
      subtotal,
      tax,
      delivery_fee: fee,
      tip,
      total,
    };
  };

  const handleCustomerSelected = (customer: Customer) => {
    setSelectedCustomer(customer);
    if (customer.addresses && customer.addresses.length > 0) {
      // Find default address or use first one
      const defaultAddress =
        customer.addresses.find((addr) => addr.is_default) ||
        customer.addresses[0];

      setSelectedAddress(defaultAddress);
    } else {
      setSelectedAddress(null);
    }
  };

  const handleAddItem = () => {
    // Validar campos requeridos
    if (!currentItem.product_id) {
      setError("Please select a product");

      return;
    }

    if (!selectedProduct) return;

    // Obtener nombre y precio del producto
    const productName = selectedProduct.name;
    let price = selectedProduct.price;
    let productTypeName = null;

    // Obtener nombre y precio del tipo de producto si aplica
    if (currentItem.product_type_id && selectedProductTypes.length > 0) {
      const productType = selectedProductTypes.find(
        (type) => type.id === currentItem.product_type_id,
      );

      if (productType) {
        price = productType.price;
        productTypeName = productType.name;
      }
    }

    // Calcular selecciones y sus precios
    const selections: OrderItemSelectionInput[] = [];
    let selectionsTotal = 0;

    if (selectedProduct.addons) {
      Object.entries(currentItem.addons).forEach(
        ([addonIdStr, selectionIds]) => {
          const addonId = parseInt(addonIdStr, 10);
          const addon = selectedProduct.addons?.find((a) => a.id === addonId);

          if (addon && selectionIds.length > 0) {
            selectionIds.forEach((selectionId) => {
              const selection = addon.selections?.find(
                (s) => s.id === selectionId
              );
              if (selection) {
                let selectionPrice = 0;
                if (selection.prices && selection.prices.length > 0) {
                  const matchingPrice = selection.prices.find((p) =>
                    currentItem.product_type_id
                      ? p.kind === currentItem.product_type_id
                      : p.kind === 1
                  );
                  if (matchingPrice) {
                    selectionPrice = matchingPrice.price;
                  } else if (selection.prices.length > 0) {
                    selectionPrice = selection.prices[0].price;
                  }
                }

                selections.push({
                  selection_id: selectionId,
                  quantity: 1,
                  price: selectionPrice,
                });

                selectionsTotal += selectionPrice;
              }
            });
          }
        }
      );
    }

    // Calcular el precio total considerando la cantidad
    const totalPrice = (price + selectionsTotal) * currentItem.quantity;

    // Crear nuevo elemento del pedido
    const newItem: OrderItemInput = {
      product_id: currentItem.product_id,
      product_type_id: currentItem.product_type_id,
      quantity: currentItem.quantity,
      unit_price: price,
      total_price: totalPrice,
      special_instructions: currentItem.special_instructions,
      product_name: productName,
      product_type_name: productTypeName,
      selections,
    };

    if (editingItemIndex !== null) {
      const updatedItems = [...orderItems];

      updatedItems[editingItemIndex] = newItem;
      setOrderItems(updatedItems);
      setEditingItemIndex(null);
    } else {
      setOrderItems([...orderItems, newItem]);
    }

    setCurrentItem({
      product_id: 0,
      product_type_id: null,
      quantity: 1,
      special_instructions: "",
      addons: {},
    });

    onClose();
    setSelectedProductId(null);
    setSelectedProduct(null);
  };

  const handleEditItem = (index: number) => {
    const item = orderItems[index];

    setEditingItemIndex(index);

    // Find the product
    const product = products.find((p) => p.id === item.product_id);

    if (product) {
      setSelectedProduct(product);
      setSelectedProductId(product.id);

      // Get product types
      if (product.product_types) {
        setSelectedProductTypes(product.product_types);
      }
    }

    // Reconstruct addons from selections
    const addons: Record<number, number[]> = {};

    if (item.selections) {
      // Group selections by addon
      item.selections.forEach((selection) => {
        // This requires you to have a mapping from selection to addon
        // This is a placeholder - you'll need to implement the actual logic
        // based on your data structure
        const addonId = 1; // Placeholder - get the actual addon ID for this selection

        if (!addons[addonId]) {
          addons[addonId] = [];
        }
        addons[addonId].push(selection.selection_id);
      });
    }

    setCurrentItem({
      product_id: item.product_id,
      product_type_id: item.product_type_id || null,
      quantity: item.quantity,
      special_instructions: item.special_instructions || "",
      addons,
    });

    onOpen();
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...orderItems];

    updatedItems.splice(index, 1);
    setOrderItems(updatedItems);
  };

  const handleSubmitOrder = async () => {
    try {
      // Validate order
      if (!selectedCustomer) {
        setError("Please select a customer");

        return;
      }

      if (orderType === "delivery" && !selectedAddress) {
        setError("Please select a delivery address");

        return;
      }

      if (!paymentMethodId) {
        setError("Please select a payment method");

        return;
      }

      if (orderItems.length === 0) {
        setError("Please add at least one item to the order");

        return;
      }

      setSubmitting(true);
      setError("");

      const totals = calculateTotals();

      // Prepare order data
      const orderData: OrderInput = {
        customer_id: selectedCustomer.id,
        order_type: orderType,
        payment_method_id: paymentMethodId,
        subtotal: totals.subtotal,
        tax: totals.tax,
        delivery_fee: totals.delivery_fee,
        tip: totals.tip,
        total: totals.total,
        items: orderItems,
        notes: notes,
        is_paid: false,
      };

      // Add delivery address if applicable
      if (orderType === "delivery" && selectedAddress) {
        orderData.delivery_address_id = selectedAddress.id;
      }

      // Submit order
      const response = await ordersService.createOrder(orderData);

      // Navigate to order details page
      navigate(`/orders/${response.id}`);
    } catch (err) {
      console.error("Error creating order:", err);
      setError("Failed to create order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-6">
        <Spinner size="lg" />
      </div>
    );
  }

  const totals = calculateTotals();

  // Obtener el tipo de producto seleccionado actualmente
  const getSelectedProductTypeInfo = () => {
    if (!currentItem.product_type_id || selectedProductTypes.length === 0) {
      return null;
    }

    const selectedType = selectedProductTypes.find(
      (type) => type.id === currentItem.product_type_id
    );

    return selectedType
      ? {
          id: selectedType.id,
          name: selectedType.name,
          price: selectedType.price,
        }
      : null;
  };

  // Información del tipo de producto seleccionado
  const selectedTypeInfo = getSelectedProductTypeInfo();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
      {/* Left column - Order Information */}
      <div className="lg:col-span-2 space-y-6">
        <Card className="p-4 space-y-4">
          <h2 className="text-xl font-semibold">Order Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <h3 className="text-md font-medium">Customer</h3>
              <CustomerSearch onSelect={handleCustomerSelected} />

              {selectedCustomer && (
                <div className="p-3 border rounded-md">
                  <div className="font-medium">
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </div>
                  <div className="text-sm">{selectedCustomer.phone_number}</div>
                  {selectedCustomer.email && (
                    <div className="text-sm">{selectedCustomer.email}</div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-medium">Order Type</h3>
              <div className="flex space-x-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    isSelected={orderType === "pickup"}
                    onValueChange={() => setOrderType("pickup")}
                  />
                  <span>Pickup</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    isSelected={orderType === "delivery"}
                    onValueChange={() => setOrderType("delivery")}
                  />
                  <span>Delivery</span>
                </div>
              </div>

              {orderType === "delivery" && selectedCustomer && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Delivery Address</h4>
                  {selectedCustomer.addresses &&
                  selectedCustomer.addresses.length > 0 ? (
                    <Select
                      label="Select Address"
                      placeholder="Select an address"
                      selectedKeys={
                        selectedAddress ? [selectedAddress.id.toString()] : []
                      }
                      onChange={(e) => {
                        const addressId = parseInt(e.target.value);
                        const address =
                          selectedCustomer.addresses?.find(
                            (a) => a.id === addressId,
                          ) || null;

                        setSelectedAddress(address);
                      }}
                    >
                      {selectedCustomer.addresses.map((address) => (
                        <SelectItem key={address.id} textValue={`${address.address_line1}, ${address.city}`}>
                          {address.address_line1}, {address.city}
                        </SelectItem>
                      ))}
                    </Select>
                  ) : (
                    <div className="text-sm text-danger">
                      No addresses available. Please add an address for this
                      customer.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-md font-medium mb-2">Payment Method</h3>
            <Select
              label="Payment Method"
              placeholder="Select payment method"
              onChange={(e) => setPaymentMethodId(parseInt(e.target.value))}
            >
              {paymentMethods.map((method) => (
                <SelectItem key={method.id} textValue={method.name}>
                  {method.name}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div>
            <h3 className="text-md font-medium mb-2">Order Notes</h3>
            <Textarea
              placeholder="Add any special instructions or notes for this order"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </Card>

        <Card className="p-4 space-y-4">
          <h2 className="text-xl font-semibold">Order Items</h2>

          {/* Product Selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-md font-medium mb-2">Category</h3>
              <Select
                items={categories}
                label="Select Category"
                placeholder="Select a category"
                onChange={(e) => setSelectedCategory(parseInt(e.target.value))}
              >
                {(category) => (
                  <SelectItem key={category.id} textValue={category.name}>
                    {category.name}
                  </SelectItem>
                )}
              </Select>
            </div>

            <div>
              <h3 className="text-md font-medium mb-2">Product</h3>
              <Select
                isDisabled={!selectedCategory}
                items={filteredProducts}
                label="Select Product"
                placeholder={
                  selectedCategory
                    ? "Select a product"
                    : "Select a category first"
                }
                onChange={(e) => setSelectedProductId(parseInt(e.target.value))}
              >
                {(product) => (
                  <SelectItem
                    key={product.id}
                    textValue={`${product.name} - ${formatPrice(product.price)}`}
                  >
                    {product.name} - {formatPrice(product.price)}
                  </SelectItem>
                )}
              </Select>
            </div>
          </div>

          {selectedProduct && (
            <div className="space-y-4">
              {/* SOLUCIÓN: Aquí modificamos la sección de tipos de producto */}
              {/* Si hay tipos de producto disponibles y no hay uno seleccionado, mostramos el selector */}
              {selectedProductTypes.length > 0 && !selectedTypeInfo ? (
                <div>
                  <h3 className="text-md font-medium mb-2">Product Type</h3>
                  <Select
                    items={selectedProductTypes}
                    label="Select Type"
                    placeholder="Select product type"
                    onChange={(e) => {
                      const typeId = parseInt(e.target.value);

                      setSelectedProductTypeId(typeId);
                      setCurrentItem({
                        ...currentItem,
                        product_type_id: typeId,
                      });
                    }}
                  >
                    {(item) => (
                      <SelectItem
                        key={item.id}
                        textValue={`${item.name} - ${formatPrice(item.price)}`}
                      >
                        {item.name} - {formatPrice(item.price)}
                      </SelectItem>
                    )}
                  </Select>
                </div>
              ) : selectedTypeInfo ? (
                // Si ya hay un tipo seleccionado, mostramos la información y un botón para cambiar
                <div>
                  <h3 className="text-md font-medium mb-2">Product Type</h3>
                  <div className="flex items-center justify-between p-3 border rounded-md">
                    <div>
                      <span className="font-medium">
                        {selectedTypeInfo.name}
                      </span>{" "}
                      - {formatPrice(selectedTypeInfo.price)}
                    </div>
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => {
                        // Permite volver a seleccionar un tipo al hacer clic
                        setCurrentItem({
                          ...currentItem,
                          product_type_id: null,
                        });
                      }}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              ) : null}

              {/* Addon selections would go here - simplified for brevity */}
              {filteredAddons && filteredAddons.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-md font-medium">Addons & Toppings</h3>
                  {filteredAddons.map((addon) => (
                    <div key={addon.id} className="border p-3 rounded-md">
                      <h4 className="font-medium">
                        {addon.name}{" "}
                        {addon.kind === 1 ? "(Required)" : "(Optional)"}
                      </h4>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {addon.selections?.map((selection) => {
                          // Encontrar el precio correcto para este tipo de producto
                          let selectionPrice = 0;
                          if (selection.prices && selection.prices.length > 0) {
                            // Buscar el precio específico para este product_type
                            const matchingPrice = selection.prices.find(
                              (p) => p.kind === selectedProductTypeId
                            );

                            if (matchingPrice) {
                              selectionPrice = matchingPrice.price;
                            } else if (selection.prices.length > 0) {
                              // Usar el primer precio como fallback
                              selectionPrice = selection.prices[0].price;
                            }
                          }

                          // Verificar si esta selección ya está seleccionada
                          const isSelected = currentItem.addons[
                            addon.id
                          ]?.includes(selection.id);

                          return (
                            <div
                              key={selection.id}
                              className="flex items-center"
                            >
                              <Switch
                                isSelected={isSelected}
                                onValueChange={(checked) => {
                                  const updatedAddons = {
                                    ...currentItem.addons,
                                  };
                                  if (!updatedAddons[addon.id]) {
                                    updatedAddons[addon.id] = [];
                                  }

                                  if (checked) {
                                    // Añadir selección
                                    updatedAddons[addon.id] = [
                                      ...updatedAddons[addon.id],
                                      selection.id,
                                    ];
                                  } else {
                                    // Eliminar selección
                                    updatedAddons[addon.id] = updatedAddons[
                                      addon.id
                                    ].filter((id) => id !== selection.id);
                                  }

                                  setCurrentItem({
                                    ...currentItem,
                                    addons: updatedAddons,
                                  });
                                }}
                              />
                              <span className="ml-2">
                                {selection.name}{" "}
                                {selectionPrice > 0 &&
                                  `(+${formatPrice(selectionPrice)})`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Si no hay addons disponibles para este tipo, mostrar un mensaje
                <div className="text-gray-500 py-2">
                  No add-ons available for this size.
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-md font-medium mb-2">Quantity</h3>
                  <Input
                    min={1}
                    type="number"
                    value={currentItem.quantity.toString()}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        quantity: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
                <div>
                  <h3 className="text-md font-medium mb-2">
                    Special Instructions
                  </h3>
                  <Textarea
                    placeholder="Special instructions for this item"
                    value={currentItem.special_instructions}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        special_instructions: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  color="primary"
                  startContent={<Plus size={16} />}
                  onPress={handleAddItem}
                >
                  {editingItemIndex !== null ? "Update Item" : "Add to Order"}
                </Button>
              </div>
            </div>
          )}

          <Divider />

          {/* Order Items List */}
          <div className="space-y-3">
            {orderItems.length === 0 ? (
              <div className="text-center p-6 border border-dashed rounded-md">
                <ShoppingCart className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500">
                  No items in cart. Add items to continue.
                </p>
              </div>
            ) : (
              orderItems.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between p-3 border rounded-md"
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{item.product_name}</span>
                      <span>
                        {formatPrice((item.total_price ?? 0) * item.quantity)}
                      </span>
                    </div>
                    {item.product_type_name && (
                      <div className="text-sm text-gray-600">
                        Type: {item.product_type_name}
                      </div>
                    )}
                    <div className="text-sm">
                      Qty: {item.quantity} * {formatPrice(item.unit_price ?? 0)}
                    </div>

                    {/* Display selections */}
                    {item.selections && item.selections.length > 0 && (
                      <div className="mt-1">
                        <div className="text-sm font-medium">Addons:</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.selections.map((selection, selIdx) => (
                            <Chip key={selIdx} size="sm" variant="flat">
                              +{formatPrice(selection.price)}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.special_instructions && (
                      <div className="mt-1 text-sm italic">
                        Notes: {item.special_instructions}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => handleEditItem(index)}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      isIconOnly
                      color="danger"
                      size="sm"
                      variant="light"
                      onPress={() => handleRemoveItem(index)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Right column - Order Summary */}
      <div className="lg:col-span-1">
        <Card className="p-4 space-y-4 sticky top-4">
          <h2 className="text-xl font-semibold">Order Summary</h2>

          {error && (
            <div className="bg-danger-50 text-danger p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatPrice(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax ({taxRate}%)</span>
              <span>{formatPrice(totals.tax)}</span>
            </div>
            {orderType === "delivery" && (
              <div className="flex justify-between">
                <span>Delivery Fee</span>
                <span>{formatPrice(totals.delivery_fee)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span>Tip</span>
              <Input
                className="w-24"
                min={0}
                size="sm"
                startContent={
                  <div className="pointer-events-none flex items-center">
                    <span className="text-default-400 text-small">$</span>
                  </div>
                }
                type="number"
                value={(tip / 100).toString()}
                onChange={(e) =>
                  setTip(Math.round(parseFloat(e.target.value) * 100) || 0)
                }
              />
            </div>
            <Divider />
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span>{formatPrice(totals.total)}</span>
            </div>
          </div>

          <Button
            className="w-full"
            color="primary"
            isDisabled={
              orderItems.length === 0 ||
              !selectedCustomer ||
              !paymentMethodId ||
              (orderType === "delivery" && !selectedAddress)
            }
            isLoading={submitting}
            size="lg"
            onPress={handleSubmitOrder}
          >
            Place Order
          </Button>

          <div className="text-center text-sm text-gray-500">
            {!selectedCustomer && <p>Please select a customer</p>}
            {orderType === "delivery" &&
              selectedCustomer &&
              !selectedAddress && <p>Please select a delivery address</p>}
            {!paymentMethodId && <p>Please select a payment method</p>}
            {orderItems.length === 0 && <p>Please add at least one item</p>}
          </div>
        </Card>
      </div>

      {/* Edit Item Modal */}
      <Modal isOpen={isOpen} size="3xl" onClose={onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {editingItemIndex !== null ? "Edit Item" : "Add Item"}
              </ModalHeader>
              <ModalBody>
                {selectedProduct && (
                  <div className="space-y-4">
                    <div className="font-medium text-lg">
                      {selectedProduct.name}
                    </div>

                    {selectedProductTypes.length > 0 && (
                      <div>
                        <h3 className="text-md font-medium mb-2">
                          Product Type
                        </h3>
                        <Select
                          label="Select Type"
                          placeholder="Select product type"
                          selectedKeys={
                            currentItem.product_type_id
                              ? [currentItem.product_type_id.toString()]
                              : []
                          }
                          onChange={(e) => {
                            const typeId = parseInt(e.target.value);

                            setSelectedProductTypeId(typeId);
                            setCurrentItem({
                              ...currentItem,
                              product_type_id: typeId,
                            });
                          }}
                        >
                          {selectedProductTypes.map((type) => (
                            <SelectItem key={type.id} textValue={type.name + " - " + formatPrice(type.price)}>
                              {type.name} - {formatPrice(type.price)}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    )}

                    {/* Addons same as above */}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-md font-medium mb-2">Quantity</h3>
                        <Input
                          min={1}
                          type="number"
                          value={currentItem.quantity.toString()}
                          onChange={(e) =>
                            setCurrentItem({
                              ...currentItem,
                              quantity: parseInt(e.target.value) || 1,
                            })
                          }
                        />
                      </div>
                      <div>
                        <h3 className="text-md font-medium mb-2">
                          Special Instructions
                        </h3>
                        <Textarea
                          placeholder="Special instructions for this item"
                          value={currentItem.special_instructions}
                          onChange={(e) =>
                            setCurrentItem({
                              ...currentItem,
                              special_instructions: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleAddItem}>
                  {editingItemIndex !== null ? "Update Item" : "Add to Order"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
